// Labels and form payloads for the agent Plan Notes page (pages/plan-notes.vue)
// and the shared note form (components/PlanNoteForm.vue). A note either pins
// one plan (`plan_id`), or is a rule: a carrier (none = every carrier; a bare
// company name covers its rate classes), a state rule, a plan type and plan
// subtypes. Oracle's endpoints/plan_note_search.py holds the matching rule.
import { carrierCompanyName } from '~/util/plan-name'

export const PLAN_TYPE_OPTIONS = [
    { value: null, text: 'All plan types' },
    { value: 'supp', text: 'Supplement' },
    { value: 'mapd', text: 'Medicare Advantage' },
    { value: 'ma', text: 'Medicare Advantage (no Rx)' },
    { value: 'pdp', text: 'Prescription Drug' },
]

export const planTypeText = value => (PLAN_TYPE_OPTIONS.find(o => o.value === value) || {}).text || value || ''

// Agents match catalog names, so this is the curated name or the raw one,
// never the client-facing label transform CurrentPlanStep applies.
export const planDisplayName = plan => (plan?.preferred_name || '').trim() || plan?.name || ''

// The raw catalog name, when a preferred name replaced it.
export const planAka = (plan) => {
    const shown = planDisplayName(plan)
    return plan?.name && plan.name !== shown ? plan.name : ''
}

export const describeStates = (note) => {
    const states = note?.states || []
    if (note?.states_mode === 'include') {
        return states.length ? `Only ${states.join(', ')}` : 'No states'
    }
    if (note?.states_mode === 'exclude' && states.length) {
        return `All except ${states.join(', ')}`
    }
    return 'All states'
}

export const describePlanStates = (states) => {
    if (!states?.length) { return 'No states listed' }
    return states.length <= 3 ? states.join(', ') : `${states.length} states`
}

const SUBTYPES_LISTED = 4

export const describeSubtypes = (subtypes) => {
    if (!subtypes?.length) { return '' }
    return subtypes.length <= SUBTYPES_LISTED ? subtypes.join(', ') : `${subtypes.length} subtypes`
}

// What a note covers, in one line. `contextPlan` is the plan row a note is
// listed under (the Plans tab), where a pinned note is simply that plan's.
export const describeAppliesTo = (note, contextPlan = null) => {
    if (note?.plan_id) {
        if (contextPlan && contextPlan.id === note.plan_id) { return 'This plan only' }
        const plan = note.plan
        return plan ? `${planDisplayName(plan)} (${plan.year})` : `Missing plan #${note.plan_id}`
    }
    const parts = [note?.carrier || 'All carriers']
    if (note?.plan_type) { parts.push(planTypeText(note.plan_type)) }
    const subtypes = describeSubtypes(note?.plan_subtype)
    if (subtypes) { parts.push(subtypes) }
    return parts.join(' · ')
}

const CARRY_OVER = {
    copied: (carry, next) => ({ text: `Copied to ${next} (#${carry.next_note_id})`, variant: 'success' }),
    missing: (carry, next) => ({ text: `${next} plan has no copy`, variant: 'warning' }),
    differs: (carry, next) => ({ text: `${next} plan has a different note`, variant: 'info' }),
    unlinked: (carry, next) => ({ text: `No linked ${next} plan`, variant: 'secondary' }),
}

// A pinned note's copy on next year's plan (oracle's `carry_over`).
export const carryOverLabel = (note, year) => {
    const carry = note?.carry_over
    const make = carry && CARRY_OVER[carry.status]
    return make ? make(carry, year + 1) : null
}

// A rule note's reach in the year; 0 means it covers nothing any more.
export const planCountLabel = (note, year) => {
    if (note?.plan_id || typeof note?.plan_count !== 'number') { return null }
    if (!note.plan_count) { return { text: `No ${year} plans`, variant: 'danger' } }
    const plans = note.plan_count === 1 ? 'plan' : 'plans'
    return { text: `Covers ${note.plan_count} ${plans} in ${year}`, variant: 'light' }
}

const covers = (noteCarrier, carrier) => noteCarrier === carrier || noteCarrier === carrierCompanyName(carrier)

// The carrier a new rule note starts with for a picked carrier group: the
// group's display name when a note on it covers every variant in the group
// (exact, or the bare company name), else the first variant. "Humana (Humana
// Benefit Plan of Illinois, Inc.) (15% Disc)" groups under a name that
// doesn't cover it.
export const prefillCarrier = (group) => {
    if (!group) { return '' }
    const variants = group.variants?.length ? group.variants : [group.name]
    return variants.every(v => covers(group.name, v)) ? group.name : variants[0]
}

export const emptyNoteForm = () => ({
    note: '',
    scope: 'rule',
    plan: null,
    carrier: '',
    states_mode: 'all',
    states: [],
    plan_type: null,
    plan_subtype: [],
    active: true,
})

export const noteToForm = note => ({
    ...emptyNoteForm(),
    note: note.note || '',
    scope: note.plan_id ? 'plan' : 'rule',
    plan: note.plan_id ? (note.plan || { id: note.plan_id }) : null,
    carrier: note.carrier || '',
    states_mode: note.states_mode === 'include' || note.states_mode === 'exclude' ? note.states_mode : 'all',
    states: [...(note.states || [])],
    plan_type: note.plan_type || null,
    plan_subtype: [...(note.plan_subtype || [])],
    active: note.active !== false,
})

export const noteFormError = (form) => {
    if (!(form.note || '').trim()) { return 'Write the note first.' }
    if (form.scope === 'plan' && !form.plan?.id) { return 'Pick the plan this note is for.' }
    if (form.scope === 'rule' && form.states_mode !== 'all' && !form.states.length) {
        return `Pick at least one state to ${form.states_mode}.`
    }
    return null
}

// Every scoping field, always, nulls included: the update forwards exactly
// what it gets, so a cleared field is written as NULL.
export const noteFormPayload = (form) => {
    const base = { note: form.note.trim(), active: !!form.active }
    if (form.scope === 'plan') {
        return { ...base, plan_id: form.plan.id, carrier: null, states: null, states_mode: null, plan_type: null, plan_subtype: null }
    }
    const limited = form.states_mode !== 'all'
    return {
        ...base,
        plan_id: null,
        carrier: (form.carrier || '').trim() || null,
        states_mode: limited ? form.states_mode : 'all',
        states: limited ? [...form.states] : null,
        plan_type: form.plan_type || null,
        plan_subtype: form.plan_subtype.length ? [...form.plan_subtype] : null,
    }
}

// The same note on another plan, every field as it is, as the oracle-spa
// dashboard's carry-over query copies them.
export const copyNotePayload = (note, planId) => ({
    note: note.note,
    plan_id: planId,
    carrier: note.carrier ?? null,
    states: note.states ?? null,
    states_mode: note.states_mode ?? null,
    plan_type: note.plan_type ?? null,
    plan_subtype: note.plan_subtype ?? null,
    active: note.active !== false,
})
